<template>
  <div>
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="$router.back()" />
      </template>
      申込条件確認
    </Header>

    <div class="mb-5 pb-5 bg-white">
      <div
        v-for="(conditions, conditionName) in allConditions"
        :key="conditionName"
      >
        <div v-if="conditions.items.length > 0" class="p-3">
          <h2
            class="pb-2 border-bottom border-color-gray03 text-size-14 text-weight-500 text-primary-dark"
          >
            {{ conditions.head }}
          </h2>
          <ul>
            <li
              v-for="(condition, index) in conditions.items"
              :key="index"
              class="req-list-item"
              :class="abTest3401Class"
              @click="onClickListItemCheck(conditionName, index)"
            >
              <template v-if="isAbTest3401TestGroup">
                <span
                  class="req-list-item-text"
                  :class="{ 'checked-text': condition.isChecked }"
                  >{{ condition.text }}</span
                >
                <SimpleCheckbox
                  :checked="condition.isChecked"
                  label=""
                  class="req-item-indicator"
                  @click.stop.prevent
                />
              </template>
              <template v-else>
                {{ condition.text }}
                <FeatherIcon
                  v-show="!condition.isChecked"
                  icon="circle"
                  size="24"
                  class="ml-2 flex-shrink-0"
                />
                <FeatherIcon
                  v-show="condition.isChecked"
                  icon="check-circle"
                  size="24"
                  class="ml-2 flex-shrink-0 text-green"
                />
              </template>
            </li>
          </ul>
        </div>
      </div>

      <div class="p-3">
        <h2
          class="pb-2 border-bottom border-color-gray03 text-size-14 text-weight-500 text-primary-dark"
        >
          {{ routeCondition.head }}
        </h2>
        <ConfirmRoute
          :latitude="latitude"
          :longitude="longitude"
          @confirm-route="confirmRoute"
        />
        <ul>
          <li
            class="req-list-item"
            :class="abTest3401Class"
            v-for="(condition, index) in routeCondition.items"
            :key="index"
            @click="onClickRouteCondition(index)"
          >
            <template v-if="isAbTest3401TestGroup">
              <span
                class="req-list-item-text"
                :class="{ 'checked-text': condition.isChecked }"
                >{{ condition.text }}</span
              >
              <SimpleCheckbox
                :checked="condition.isChecked"
                label=""
                class="req-item-indicator"
                @click.stop.prevent
              />
            </template>
            <template v-else>
              {{ condition.text }}
              <FeatherIcon
                v-show="!condition.isChecked"
                icon="circle"
                size="24"
                class="ml-2 flex-shrink-0"
              />
              <FeatherIcon
                v-show="condition.isChecked"
                icon="check-circle"
                size="24"
                class="ml-2 flex-shrink-0 text-green"
              />
            </template>
          </li>
        </ul>
      </div>

      <OrderFixedButton
        v-show="!canNext"
        hasSub
        isDisabled
        :abTestGroup3300="abTestGroup3300"
      >
        次へ
        <template #sub>
          全てチェックする必要があります
        </template>
      </OrderFixedButton>

      <OrderFixedButton
        v-show="canNext"
        enabledVariant="primary"
        @click="onClickNext"
        :abTestGroup3300="abTestGroup3300"
      >
        次へ
      </OrderFixedButton>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-facing-decorator";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import Header from "@/components/organisms/common/Header.vue";
import OrderFixedButton from "@/components/organisms/orders/OrderFixedButton.vue";
import ConfirmRoute from "@/components/organisms/common/ConfirmRoute.vue";
import SimpleCheckbox from "@/components/atoms/input/SimpleCheckbox.vue";
import { orderStore } from "@/store/order";
import { sendAdjustEvent } from "@/plugins/adjust";
import { sendFirebaseCustomEvent } from "@/plugins/firebase";
import { openInNewWindow } from "@/utils/browserUtil";
import { makeGoogleMapConfirmRouteUrl } from "@/utils/googlemap";
import { orderService } from "@/app/services/OrderService";
import { OrderDetail } from "@/types/order/Order";
import { authStore } from "@/store/auth";
import { AbTest3300, AbTest3401 } from "@/app/services/AbTestService";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const cordova: any;

interface Item {
  text: string;
  isChecked: boolean;
}

@Component({
  components: {
    FeatherIcon,
    Header,
    OrderFixedButton,
    ConfirmRoute,
    SimpleCheckbox
  }
})
export default class OrdersRequirementConfirmation extends Vue {
  allConditions = {
    workTime: {
      head: "勤務時間",
      items: [] as Item[]
    },
    otherCondition: {
      head: "経験・スキル",
      items: [] as Item[]
    },
    clothesCondition: {
      head: "服装・身だしなみ",
      items: [] as Item[]
    },
    belongings: {
      head: "持ち物・その他の条件",
      items: [] as Item[]
    }
  };
  // NOTE: head や items の text は変更されないので computed に持っても良さそうだが、上記項目に合わせて data として保持している
  routeCondition = {
    head: "勤務地",
    items: [{ text: "通勤経路、通勤時間は確認しましたか？", isChecked: false }]
  };

  get orderId() {
    return Number(this.$route.params["orderId"]);
  }
  get workTimeOptionId() {
    return Number(this.$route.params["workTimeOptionId"]);
  }

  get orderDetail(): OrderDetail {
    return orderStore.orderDetail!;
  }

  get workTimeOption() {
    return this.orderDetail.workTimeOptions.find(
      w => w.id === this.workTimeOptionId
    );
  }

  get latitude() {
    return this.orderDetail.office.latitude;
  }

  get longitude() {
    return this.orderDetail.office.longitude;
  }

  get canNext() {
    return (
      this.allConditions.workTime.items.every(c => c.isChecked) &&
      this.allConditions.otherCondition.items.every(c => c.isChecked) &&
      this.allConditions.clothesCondition.items.every(c => c.isChecked) &&
      this.allConditions.belongings.items.every(c => c.isChecked) &&
      this.routeCondition.items.every(c => c.isChecked)
    );
  }

  get abTestGroup3300(): "B" | "C" {
    return AbTest3300.getTestGroup();
  }

  get abTestGroup3401(): "A" | "B" {
    return AbTest3401.getTestGroup();
  }

  get isAbTest3401TestGroup(): boolean {
    return this.abTestGroup3401 !== "A";
  }

  get abTest3401Class(): string | null {
    const testGroup = AbTest3401.getTestGroup();
    if (!this.isAbTest3401TestGroup) {
      return null;
    }
    return `ab-test-group-3401-${testGroup.toLowerCase()}`;
  }

  created() {
    this.createCheckList();
  }

  mounted() {
    AbTest3401.registerEvent(AbTest3401.eventTypes.entry);
  }

  createCheckList() {
    Object.keys(this.allConditions).forEach(
      name =>
        // @ts-ignore
        (this.allConditions[name].items = [...Array(5)]
          .map((_, i) => ({
            text: this.orderDetail?.[`${name}${i + 1}` as keyof OrderDetail],
            isChecked: false
          }))
          .filter(e => e.text))
    );
    const workTimeLabel = orderService.getWorkTimeRangeWithRestTimeFromWorkTimeOption(
      this.workTimeOption!
    );
    this.allConditions.workTime.items = [
      {
        text: `勤務時間が${workTimeLabel}\nであることを確認しました`,
        isChecked: false
      }
    ];
  }

  onClickListItemCheck(listName: string, index: number) {
    const key = listName as keyof typeof this.allConditions;

    // ABテストグループBの場合
    if (this.isAbTest3401TestGroup) {
      // 常にtrueに設定する（一度チェックしたら外せない仕様）
      this.allConditions[key].items[index].isChecked = true;
      return;
    }

    this.allConditions[key].items[index].isChecked = !this.allConditions[key]
      .items[index].isChecked;
  }

  onClickRouteCondition(index: number) {
    // ABテストグループBの場合
    if (this.isAbTest3401TestGroup) {
      // 常にtrueに設定する（一度チェックしたら外せない仕様）
      this.routeCondition.items[index].isChecked = true;
      return;
    }

    this.routeCondition.items[index].isChecked = !this.routeCondition.items[
      index
    ].isChecked;
  }

  async onClickNext() {
    sendAdjustEvent("CONFIRM_APPLICATION_CONDITION");
    sendFirebaseCustomEvent("confirm_application_condition", {
      session_id: authStore.sessionId || "unknown"
    });

    await this.$router.push({
      name: "OrdersCancelPolicy",
      params: {
        orderId: this.orderId.toString(),
        workTimeOptionId: this.workTimeOptionId.toString()
      }
    });
  }

  confirmRoute() {
    if (!this.latitude || !this.longitude) {
      throw new Error(
        "latitude,longitudeが未設定の事業所の「Google Mapでルート確認」ボタンが押下されました。"
      );
    }

    /**
     * HACK: 本来はcordova-plugin-white-listで制限しているため自動で外部ブラウザに移動するが、googleMapのiframeの読み込みが必要なため、
     * googleMapのルートに移動する時だけプラグインを利用する
     */
    openInNewWindow(
      makeGoogleMapConfirmRouteUrl(this.latitude, this.longitude)
    );
  }
}
</script>

<style lang="scss" scoped>
.line-height-14 {
  line-height: 14px;
}

.req-list-item {
  display: flex;
  justify-content: space-between;
  padding-top: 12px;
  padding-bottom: 12px;
  color: $mild;
  white-space: pre-wrap;
  border-bottom: 1px solid $gray-03;
}

/* ABテスト3401のグループB専用スタイル */
.ab-test-group-3401-b {
  display: flex;
  align-items: center;

  .req-list-item-text {
    display: flex;
    flex: 1;
    align-items: center;
    color: #5a6e7e;

    &.checked-text {
      color: #1c1c1c;
    }
  }

  .req-item-indicator {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    margin-left: 0.5rem;
    pointer-events: none;
  }
}
</style>
